import React, { useEffect, useMemo, useState } from 'react';
import { Building2, Loader2, RotateCcw, Save } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { CampoImpostazione } from './CampoImpostazione';
import { BRANDING_DA_ENV, soloCifre, type Branding } from '../../lib/branding';
import { useSettingsStore } from '../../store/useSettingsStore';
import { useNotifications } from '../../store/useStore';

/**
 * Dati anagrafici e fiscali dell'azienda.
 *
 * Questi valori finiscono su fatture, offerte e PDF: un errore qui esce
 * dall'ufficio e arriva al cliente. Per questo il salvataggio e' esplicito
 * (niente salvataggio automatico mentre si scrive) e i campi che il fisco
 * guarda sono controllati prima di partire.
 */

/** "Partita IVA" e simili: si accettano solo cifre, ma con la lunghezza giusta. */
const validaAzienda = (dati: Branding): Partial<Record<keyof Branding, string>> => {
    const errori: Partial<Record<keyof Branding, string>> = {};

    if (!dati.ragioneSociale.trim()) errori.ragioneSociale = 'La ragione sociale serve su ogni documento.';
    if (!dati.nomeBreve.trim()) errori.nomeBreve = 'Serve un nome breve per interfaccia ed email.';

    const piva = soloCifre(dati.partitaIva);
    if (dati.partitaIva.trim() && piva.length !== 11) {
        errori.partitaIva = 'La partita IVA italiana ha 11 cifre.';
    }

    const cf = dati.codiceFiscale.trim();
    if (cf && !(cf.length === 11 || cf.length === 16)) {
        errori.codiceFiscale = 'Il codice fiscale ha 11 cifre (società) o 16 caratteri (persona).';
    }

    const cap = soloCifre(dati.cap);
    if (dati.cap.trim() && cap.length !== 5) errori.cap = 'Il CAP ha 5 cifre.';

    if (dati.provincia.trim() && dati.provincia.trim().length !== 2) {
        errori.provincia = 'La provincia è la sigla di due lettere, es. PE.';
    }

    if (dati.email.trim() && !dati.email.includes('@')) errori.email = 'Indirizzo email non valido.';
    if (dati.pec.trim() && !dati.pec.includes('@')) errori.pec = 'Indirizzo PEC non valido.';

    const sdi = dati.codiceDestinatario.trim();
    if (sdi && sdi.length !== 7) errori.codiceDestinatario = 'Il codice destinatario SDI ha 7 caratteri.';

    const iban = dati.iban.replace(/\s/g, '');
    if (iban && iban.length < 15) errori.iban = 'IBAN troppo corto: controlla di averlo copiato tutto.';

    const wa = soloCifre(dati.whatsapp);
    if (dati.whatsapp.trim() && (wa.length < 8 || wa.length > 15)) {
        errori.whatsapp = 'Serve il numero col prefisso internazionale, es. 39 333 1234567.';
    }

    if (!Number.isFinite(dati.aliquotaIvaDefault) || dati.aliquotaIvaDefault < 0 || dati.aliquotaIvaDefault > 100) {
        errori.aliquotaIvaDefault = 'L’aliquota va da 0 a 100.';
    }

    return errori;
};

export function AziendaCard() {
    const { azienda, salvaAzienda } = useSettingsStore();
    const { addNotification } = useNotifications();
    const [bozza, setBozza] = useState<Branding>(azienda);
    const [salvataggio, setSalvataggio] = useState(false);
    const [mostraErrori, setMostraErrori] = useState(false);

    // Quando arriva la risposta del database la bozza riparte da li'.
    useEffect(() => setBozza(azienda), [azienda]);

    const errori = useMemo(() => validaAzienda(bozza), [bozza]);
    const modificato = useMemo(
        () => JSON.stringify(bozza) !== JSON.stringify(azienda),
        [bozza, azienda]
    );

    const campo = (chiave: keyof Branding) => (valore: string) =>
        setBozza((precedente) => ({ ...precedente, [chiave]: valore }));

    const erroreDi = (chiave: keyof Branding) => (mostraErrori ? errori[chiave] : undefined);

    const handleSalva = async () => {
        if (Object.keys(errori).length > 0) {
            setMostraErrori(true);
            addNotification({
                type: 'error',
                title: 'Controlla i campi segnati',
                message: 'Alcuni dati non sono nel formato giusto: finirebbero così su fatture e PDF.'
            });
            return;
        }

        setSalvataggio(true);
        try {
            await salvaAzienda(bozza);
            setMostraErrori(false);
            addNotification({
                type: 'success',
                title: 'Dati azienda salvati',
                message: 'I documenti creati da adesso useranno questi dati.'
            });
        } catch (error) {
            console.error('Errore salvataggio dati azienda:', error);
            addNotification({
                type: 'error',
                title: 'Non salvato',
                message: 'Il database ha rifiutato il salvataggio. Riprova, e se insiste avvisa chi gestisce l’app.'
            });
        } finally {
            setSalvataggio(false);
        }
    };

    return (
        <Card>
            <CardHeader>
                <div className="flex items-center gap-2">
                    <Building2 className="w-5 h-5 text-primary-600" />
                    <CardTitle>Dati azienda</CardTitle>
                </div>
                <CardDescription>
                    Ragione sociale, sede, dati fiscali e contatti. Finiscono su fatture, offerte,
                    PDF e nel pi&egrave; di pagina delle email.
                </CardDescription>
            </CardHeader>

            <CardContent className="space-y-6">
                <section className="space-y-3">
                    <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500">Identit&agrave;</h3>
                    <div className="grid gap-4 sm:grid-cols-2">
                        <CampoImpostazione
                            etichetta="Ragione sociale"
                            valore={bozza.ragioneSociale}
                            onChange={campo('ragioneSociale')}
                            errore={erroreDi('ragioneSociale')}
                            aiuto="Il nome completo, come va in fattura."
                        />
                        <CampoImpostazione
                            etichetta="Nome breve"
                            valore={bozza.nomeBreve}
                            onChange={campo('nomeBreve')}
                            errore={erroreDi('nomeBreve')}
                            aiuto="Usato dentro l'app e nelle email."
                        />
                    </div>
                </section>

                <section className="space-y-3">
                    <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500">Sede</h3>
                    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                        <CampoImpostazione
                            etichetta="Indirizzo"
                            valore={bozza.indirizzo}
                            onChange={campo('indirizzo')}
                            segnaposto="Via Roma, 1"
                            className="sm:col-span-2"
                        />
                        <CampoImpostazione
                            etichetta="CAP"
                            valore={bozza.cap}
                            onChange={campo('cap')}
                            errore={erroreDi('cap')}
                            segnaposto="65100"
                        />
                        <CampoImpostazione
                            etichetta="Comune"
                            valore={bozza.citta}
                            onChange={campo('citta')}
                            segnaposto="Pescara"
                        />
                        <CampoImpostazione
                            etichetta="Provincia"
                            valore={bozza.provincia}
                            onChange={(v) => campo('provincia')(v.toUpperCase())}
                            errore={erroreDi('provincia')}
                            segnaposto="PE"
                        />
                    </div>
                </section>

                <section className="space-y-3">
                    <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500">Fisco</h3>
                    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                        <CampoImpostazione
                            etichetta="Partita IVA"
                            valore={bozza.partitaIva}
                            onChange={campo('partitaIva')}
                            errore={erroreDi('partitaIva')}
                            segnaposto="01234567890"
                        />
                        <CampoImpostazione
                            etichetta="Codice fiscale"
                            valore={bozza.codiceFiscale}
                            onChange={campo('codiceFiscale')}
                            errore={erroreDi('codiceFiscale')}
                        />
                        <CampoImpostazione
                            etichetta="REA"
                            valore={bozza.rea}
                            onChange={campo('rea')}
                            aiuto="Numero di iscrizione al registro imprese."
                        />
                        <CampoImpostazione
                            etichetta="Codice destinatario SDI"
                            valore={bozza.codiceDestinatario}
                            onChange={campo('codiceDestinatario')}
                            errore={erroreDi('codiceDestinatario')}
                            aiuto="Dove arrivano le fatture che ricevete."
                        />
                        <div className="space-y-1.5">
                            <Label className="text-sm font-medium text-gray-700">Regime fiscale</Label>
                            <select
                                value={bozza.regimeFiscaleCode}
                                onChange={(e) => {
                                    const code = e.target.value;
                                    setBozza((p) => ({
                                        ...p,
                                        regimeFiscaleCode: code,
                                        regimeFiscale: code === 'RF19' ? 'Regime forfettario' : 'Regime ordinario'
                                    }));
                                }}
                                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                            >
                                <option value="RF01">Ordinario (RF01)</option>
                                <option value="RF19">Forfettario (RF19)</option>
                            </select>
                            <p className="text-xs leading-snug text-gray-500">
                                In forfettario i nuovi documenti nascono senza IVA.
                            </p>
                        </div>
                        <CampoImpostazione
                            etichetta="Aliquota IVA predefinita"
                            tipo="number"
                            valore={String(bozza.aliquotaIvaDefault)}
                            onChange={(v) => setBozza((p) => ({ ...p, aliquotaIvaDefault: Number(v) }))}
                            errore={erroreDi('aliquotaIvaDefault')}
                            aiuto="Applicata alle righe nuove dei documenti."
                        />
                    </div>
                </section>

                <section className="space-y-3">
                    <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500">Contatti</h3>
                    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                        <CampoImpostazione
                            etichetta="Telefono"
                            tipo="tel"
                            valore={bozza.telefono}
                            onChange={campo('telefono')}
                            segnaposto="+39 085 1234567"
                        />
                        <CampoImpostazione
                            etichetta="Numero WhatsApp"
                            tipo="tel"
                            valore={bozza.whatsapp}
                            onChange={campo('whatsapp')}
                            errore={erroreDi('whatsapp')}
                            segnaposto="39 333 1234567"
                            aiuto="Col prefisso del paese. Compare nelle email come 'scrivici su WhatsApp'."
                        />
                        <CampoImpostazione
                            etichetta="Email"
                            tipo="email"
                            valore={bozza.email}
                            onChange={campo('email')}
                            errore={erroreDi('email')}
                        />
                        <CampoImpostazione
                            etichetta="PEC"
                            tipo="email"
                            valore={bozza.pec}
                            onChange={campo('pec')}
                            errore={erroreDi('pec')}
                        />
                        <CampoImpostazione
                            etichetta="Sito"
                            valore={bozza.sito}
                            onChange={campo('sito')}
                            segnaposto="www.esempio.it"
                        />
                    </div>
                </section>

                <section className="space-y-3">
                    <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500">Banca</h3>
                    <div className="grid gap-4 sm:grid-cols-2">
                        <CampoImpostazione
                            etichetta="IBAN"
                            valore={bozza.iban}
                            onChange={campo('iban')}
                            errore={erroreDi('iban')}
                            aiuto="Stampato sulle fatture per il bonifico."
                        />
                        <CampoImpostazione
                            etichetta="Banca"
                            valore={bozza.banca}
                            onChange={campo('banca')}
                        />
                    </div>
                </section>

                <div className="flex flex-wrap items-center justify-end gap-2 border-t pt-4">
                    <Button
                        variant="ghost"
                        onClick={() => setBozza({ ...BRANDING_DA_ENV })}
                        disabled={salvataggio}
                        title="Rimette i valori con cui l'app era stata installata. Non salva: devi confermare."
                    >
                        <RotateCcw className="w-4 h-4 mr-2" />
                        Valori iniziali
                    </Button>
                    <Button
                        variant="outline"
                        onClick={() => setBozza(azienda)}
                        disabled={!modificato || salvataggio}
                    >
                        Annulla modifiche
                    </Button>
                    <Button onClick={handleSalva} disabled={!modificato || salvataggio}>
                        {salvataggio ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                        Salva dati azienda
                    </Button>
                </div>
            </CardContent>
        </Card>
    );
}

export default AziendaCard;
